import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const KEY = "kcc_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { console.warn("Cart load failed", e); return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) { console.warn("Cart persist failed", e); }
  }, [items]);

  const addItem = useCallback((item) => {
    // item: { id, type:'plan'|'addon', name, monthly?:number, oneTime?:number, setupFee?:number, description? }
    setItems(prev => {
      // If a plan exists, replace it (only one plan at a time)
      if (item.type === "plan") {
        const filtered = prev.filter(p => p.type !== "plan");
        return [...filtered, { ...item, qty: 1 }];
      }
      const existing = prev.find(p => p.id === item.id);
      if (existing) {
        return prev.map(p => p.id === item.id ? { ...p, qty: (p.qty || 1) + 1 } : p);
      }
      return [...prev, { ...item, qty: 1 }];
    });
  }, []);

  const removeItem = useCallback((id) => setItems(prev => prev.filter(p => p.id !== id)), []);
  const updateQty = useCallback((id, qty) => {
    setItems(prev => prev.map(p => p.id === id ? { ...p, qty: Math.max(1, qty) } : p));
  }, []);
  const clearCart = useCallback(() => setItems([]), []);

  const { subtotal, dueToday, monthly } = useMemo(() => {
    let monthlyTotal = 0;
    let oneTimeTotal = 0;
    items.forEach(it => {
      const qty = it.qty || 1;
      if (it.monthly) monthlyTotal += it.monthly * qty;
      if (it.setupFee) oneTimeTotal += it.setupFee * qty;
      if (it.oneTime) oneTimeTotal += it.oneTime * qty;
    });
    return {
      subtotal: oneTimeTotal + monthlyTotal, // for display
      dueToday: oneTimeTotal,
      monthly: monthlyTotal,
    };
  }, [items]);

  const value = { items, addItem, removeItem, updateQty, clearCart, subtotal, dueToday, monthly, count: items.reduce((n, i) => n + (i.qty || 1), 0) };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
